import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Crown, Check, Loader2, CreditCard, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PLANS = [
  { lookup_key: "pro_monthly", tier: "pro", label: "Pro", cadence: "Monthly", price: "$4.99", per: "/mo", perks: ["SMS number", "Unlimited notes & polaroids", "Custom Board Studio", "All sound packs"] },
  { lookup_key: "pro_yearly", tier: "pro", label: "Pro", cadence: "Yearly", price: "$39", per: "/yr", perks: ["Everything in Pro", "2 months free", "7-day trial"], best: true },
  { lookup_key: "family_monthly", tier: "family", label: "Family", cadence: "Monthly", price: "$8.99", per: "/mo", perks: ["Everything in Pro", "Shared boards for 5"] },
  { lookup_key: "family_yearly", tier: "family", label: "Family", cadence: "Yearly", price: "$69", per: "/yr", perks: ["Everything in Family", "2 months free"] },
];

export function UpgradeDialog({ open, onClose }) {
  const [busy, setBusy] = useState("");

  const checkout = async (lookup_key, method) => {
    setBusy(`${method}-${lookup_key}`);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="upgrade-dialog" className="max-w-lg bg-[#fbf6ec] grain max-h-[85vh] overflow-auto board-scroll">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl text-[#2b2620] flex items-center gap-2"><Crown className="w-5 h-5 text-[#ff3b30]" /> Upgrade your board</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6b6155] -mt-2">Test mode — use card <b>4242 4242 4242 4242</b>, any future date & CVC.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {PLANS.map((p) => (
            <div key={p.lookup_key} className={`relative rounded-2xl p-4 border ${p.best ? "border-[#ff3b30] bg-white" : "border-[#e6dcc8] bg-white/70"}`}>
              {p.best && <span className="absolute -top-2.5 left-4 bg-[#ff3b30] text-white tag-label px-2 py-0.5 rounded-full">Best value</span>}
              <div className="flex items-baseline justify-between">
                <span className="font-display font-black text-lg text-[#2b2620]">{p.label}</span>
                <span className="tag-label text-[#a0703c]">{p.cadence}</span>
              </div>
              <p className="font-display font-black text-2xl text-[#2b2620] mt-1">{p.price}<span className="text-sm font-bold text-[#8c8073]">{p.per}</span></p>
              <ul className="mt-2 space-y-1">
                {p.perks.map((x) => <li key={x} className="flex gap-1.5 text-xs text-[#4a4238]"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#34c759]" />{x}</li>)}
              </ul>
              <Button data-testid={`checkout-${p.lookup_key}`} onClick={() => checkout(p.lookup_key, "card")} disabled={!!busy} className="w-full mt-3 rounded-full bg-[#2b2620] hover:bg-black text-[#fdf6e3] font-display font-bold h-9 gap-1.5">
                {busy === `card-${p.lookup_key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Card</>}
              </Button>
              <Button data-testid={`checkout-link-${p.lookup_key}`} onClick={() => checkout(p.lookup_key, "link")} disabled={!!busy} className="w-full mt-2 rounded-full bg-[#00d66f] hover:bg-[#00c264] text-[#0b3b2e] font-display font-black h-9 gap-1.5">
                {busy === `link-${p.lookup_key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" fill="#0b3b2e" /> Pay with Link</>}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
